/* istanbul ignore file */
import { useState, useMemo, useCallback } from 'react'
import logger from '@adc/logger'
import { match } from 'ts-pattern'
import _ from 'lodash'
import classnames from 'classnames/bind'
import {
  InputField,
  SelectField,
  NumberInputField,
  CurrencyInputField,
  utils as coreUtils,
  RadioGroupField,
  AddressSuggestionField,
  MaskedInputField,
  AuthToggle,
  CheckboxField,
  RadioButtonGroupField,
  hooks,
  SelectedAddress,
} from '@adc/ui-adc'
import * as blueprint from '@adc/blueprint'
import configment from '@adc/configment'
import styles from '../styles.scss'
import Map from '../confirmAddressMap'
import { Question, QuestionType } from '../steps'
import { StepId } from '../steps/types'
import IntentSelector from '../intentSelector'
import PasswordField from '../passwordField'

const cx = classnames.bind(styles)

const {
  inputMasks: { phoneNumberMask },
  formatters: { formatPhoneNumber },
  currency,
} = coreUtils
interface LocationSuggestionsResponse {
  rawLocationSuggestions: Array<{
    value?: string
    county?: string
    city?: string
    state?: string
    zip?: string
    type?: string
    url?: string
    coordinates?: {
      lat: number
      lon: number
    }
  }>
}

export type DynamicFieldProps = {
  onChange: (
    value: Record<string, any>,
    validateOnlyOnBlur?: boolean | undefined
  ) => void
  onBlur: (
    value: Record<string, string>,
    validateOnlyOnBlur: boolean | undefined
  ) => void
  onPreviousStep: (e: Event) => void
  onNextStep: (e: Event) => void
  question: Question
  leadIntake: Record<string, any>
  error?: string
  stepProgression: {
    isNextDisabled: boolean
    isCurrentlyValidating: boolean
  }
  currentStepId?: StepId
}

export const getDefaultValue = (
  question: Question,
  leadIntake: Record<string, any>
): string => {
  if (!question.defaultValue) {
    return ''
  }

  if (typeof question.defaultValue === 'function') {
    const result = question.defaultValue(leadIntake)
    return result === null ? '' : String(result)
  }
  return question.defaultValue === null ? '' : String(question.defaultValue)
}

const DynamicField = ({
  question,
  onChange,
  leadIntake,
  onNextStep,
  error,
  stepProgression,
  onBlur,
}: DynamicFieldProps) => {
  const {
    type,
    field,
    placeholder,
    options,
    valueParser,
    maskOptions,
    showIncrementButtons,
    disabled,
    validateOnlyOnBlur = false,
    compact = false,
    readOnly,
  } = question

  const [fieldValue, setFieldValue] = useState<string>(
    leadIntake[field] || getDefaultValue(question, leadIntake)
  )
  const onChangeCallback = useCallback(
    (
      e: { target: { value: string | number | boolean } },
      meta?: { value?: string | number | boolean }
    ) => {
      const nextValue = meta?.value || e.target.value
      setFieldValue(nextValue as string)
      const nextAnswer = {
        [field]: valueParser ? valueParser(nextValue) : nextValue,
      }
      onChange(nextAnswer, validateOnlyOnBlur)
    },
    [onChange, field, valueParser, validateOnlyOnBlur]
  )

  const onBlurCallback = useCallback(
    (e: { target: { value: string } }) => {
      const currentValue = { [field]: e.target.value }
      onBlur(currentValue, validateOnlyOnBlur)
    },
    [onBlur, field, validateOnlyOnBlur]
  )

  hooks.useMountEffect(() => {
    if (!(field in leadIntake) && getDefaultValue(question, leadIntake)) {
      const syntheticEvent = {
        target: { value: getDefaultValue(question, leadIntake) },
      }
      onChangeCallback(syntheticEvent)
    }
  })

  const isDisabled =
    typeof disabled === 'function' ? disabled(leadIntake) : disabled || false
  const isReadOnly =
    typeof readOnly === 'function' ? readOnly(leadIntake) : disabled || false
  const lookupLocation = async (search: string): Promise<string | null> => {
    try {
      const result = (await blueprint.search.providers.fetchLocationSuggestions(
        search
      )) as LocationSuggestionsResponse | null

      if (!result?.rawLocationSuggestions?.[0]?.county) {
        return null
      }
      return result.rawLocationSuggestions[0].county
    } catch (error) {
      logger.error('Error looking up county:', error)
      return null
    }
  }
  const finalShowIncrementButtons = isDisabled ? false : showIncrementButtons
  const onAddressSuggestChange = (event: SelectedAddress) => {
    const addressSuggestion = _.trim(event.selectedAddressText)
    setFieldValue(addressSuggestion)

    const extractedValues = _.pick(event, [
      'municipality',
      'postal_code',
      'country_primary_subdivision',
      'country_secondary_subdivision',
      'coordinates',
      'street_name',
      'street_number',
      'street_unit',
      'country_code',
    ])
    const county =
      extractedValues.country_secondary_subdivision?.trim() || undefined

    const nextAddressValue = {
      'property.address': extractedValues.street_name,
      'property.street_number': extractedValues.street_number,
      'property.city': extractedValues.municipality,
      'property.county': county,
      'property.state': extractedValues.country_primary_subdivision,
      'property.postal_code': extractedValues.postal_code,
      'property.street_unit': extractedValues.street_unit,
      'property.country_code': extractedValues.country_code,
      '__computed__.property.coordinates': {
        lat: extractedValues.coordinates?.lat,
        lng: extractedValues.coordinates?.lon,
      },
      '__computed__.property.addressDisplayText': addressSuggestion,
      '__computed__.property.is_option_selected': event.is_option_selected,
    }
    onChange(nextAddressValue)

    if (!county && event.is_option_selected) {
      const cityState = `${extractedValues.municipality}, ${extractedValues.country_primary_subdivision}`
      lookupLocation(cityState)
        .then(lookupResult => {
          if (lookupResult) {
            const updatedAddressValue = {
              ...nextAddressValue,
              'property.county': lookupResult,
            }
            onChange(updatedAddressValue)
          }
        })
        .catch(error => {
          logger.error('Error looking up county:', error)
        })
    }
  }
  const commonProps = useMemo(
    () => ({
      placeholder,
      value: fieldValue,
      onChange: onChangeCallback,
      touched: true,
      error,
      className: cx('common-input'),
      disabled: isDisabled,
      readOnly: isReadOnly,
      elmId: field,
      ...question.componentProps,
    }),
    [error, fieldValue, placeholder, onChangeCallback, isDisabled]
  )
  return (
    <>
      {match<{ type: string }, React.ReactNode>({ type })
        .with({ type: QuestionType.Address }, () => (
          <AddressSuggestionField
            name='address_suggestion'
            elmId='property_address_suggestion_field'
            className={cx('address-suggestion')}
            value={{
              selectedAddressText: fieldValue,
            }}
            onChange={onAddressSuggestChange}
            googleApiConfig={{
              channelName: configment.get('GOOGLE_MAPS_CHANNEL_NAME', ''),
              apiKey: configment.get('GOOGLE_MAPS_API_KEY', ''),
            }}
            displayFormattedAddress
            googleAutocompleteOptions={{
              componentRestrictions: { country: ['us', 'pr'] },
            }}
            touched
            error={!stepProgression.isCurrentlyValidating && error}
          />
        ))
        .with({ type: QuestionType.Input }, () => (
          <InputField {...commonProps} onBlur={onBlurCallback} touched />
        ))
        .with({ type: QuestionType.Select }, () => (
          <SelectField {...commonProps} options={options} />
        ))
        .with({ type: QuestionType.Currency }, () => (
          <CurrencyInputField
            {...commonProps}
            onChange={(e: { target: { value: any } }) =>
              onChangeCallback(e, {
                value: currency.parse(e.target.value || 0.0),
              })
            }
          />
        ))
        .with({ type: QuestionType.Number }, () => {
          return (
            // @ts-ignore next
            <NumberInputField
              {...commonProps}
              onChange={onChangeCallback}
              maskOptions={{ allowLeadingZeroes: true, ...maskOptions }}
              showIncrementButtons={finalShowIncrementButtons}
            />
          )
        })
        .with({ type: QuestionType.Radio }, () => (
          <RadioGroupField
            value={fieldValue}
            onChange={onChangeCallback}
            options={options}
            touched
            error={error}
            compact={false}
            elmId={field}
          />
        ))
        .with({ type: QuestionType.GroupRadio }, () => (
          <div className={cx('radio-group')}>
            <RadioButtonGroupField
              elmId={field}
              value={fieldValue}
              disabled={
                typeof question.disabled === 'function'
                  ? question.disabled(leadIntake)
                  : question.disabled ?? false
              }
              name={field}
              onChange={(e: any, { value }: { value: string }) =>
                onChangeCallback(e, { value })
              }
              options={
                question.options?.map(opt => ({
                  label: opt.label,
                  subLabel: opt.subLabel as string,
                  image: opt.image as string,
                  value: opt.value as string,
                })) ?? []
              }
              {...question.componentProps}
            />
          </div>
        ))
        .with({ type: QuestionType.RadioWithDetails }, () => (
          <IntentSelector
            elmId={field}
            value={fieldValue === null ? '' : fieldValue}
            name={field}
            onChange={(e: any, { value }: { value: string }) =>
              onChangeCallback(e, { value })
            }
            options={question.options}
          />
        ))
        .with({ type: QuestionType.PhoneNumber }, () => (
          <MaskedInputField
            {...commonProps}
            format={formatPhoneNumber}
            mask={phoneNumberMask}
            inputLabel='+1'
            error={error}
            onBlur={onBlurCallback}
          />
        ))
        .with({ type: QuestionType.Map }, () => (
          <Map
            data-elm-id='property_address_confirm'
            marker={leadIntake['__computed__.property.coordinates']}
            address={leadIntake['__computed__.property.addressDisplayText']}
          />
        ))
        .with({ type: QuestionType.Authentication }, () => (
          <div className={cx('auth-toggle-wrapper')}>
            <AuthToggle
              userKind='LISTING_AGENT'
              initialActive='signup'
              onSuccess={onNextStep}
              signUpInitialValues={{
                firstName: leadIntake.first_name,
                lastName: leadIntake.last_name,
                email: leadIntake.email,
                phone: leadIntake.phone,
              }}
              cls={{
                root: cx('auth-toggle'),
              }}
              elmId='auth_toggle_signup'
            />
          </div>
        ))
        .with({ type: QuestionType.Password }, () => (
          <div className={cx('auth-toggle-wrapper')}>
            <PasswordField />
          </div>
        ))
        .with({ type: QuestionType.Checkbox }, () => (
          <CheckboxField
            elmId={`property_checkbox_${field}`}
            className={cx('input-checkbox')}
            checked={fieldValue}
            onChange={(_: any, { checked }: { checked: boolean }) => {
              onChangeCallback({ target: { value: checked } })
            }}
            label={placeholder}
            touched
            error={error}
            compact={compact}
          />
        ))
        .otherwise(() => (
          <InputField {...commonProps} />
        ))}
    </>
  )
}

export default DynamicField
